/* Iter 418.aq (Dirk, 2026-02-28) — Interactive trend chart. Replaces
   the static matplotlib PNG on the on-screen Plant Report + Embed.
   Uses Recharts so operators get hover tooltips (exact value + ISO
   timestamp) and a Brush at the bottom for zooming into a spike.
   PNG is still returned by the backend for the PDF path. */

import { useMemo } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine, Brush,
} from "recharts";

function fmtTs(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit",
  });
}

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="text-[10px] bg-slate-900 border border-white/10 rounded px-2 py-1 shadow-lg">
      <div className="text-[#a0aabf]">{fmtTs(label)}</div>
      <div className="font-mono text-white">
        {Number(p.value).toFixed(2)}{unit ? ` ${unit}` : ""}
      </div>
    </div>
  );
}

export default function InteractiveTrendChart({ chart }) {
  const data = useMemo(
    () => (chart?.series || []).map((s) => ({ ts: s.ts, v: s.v })),
    [chart],
  );
  if (data.length === 0) return null;
  const values = data.map((d) => d.v);
  const yMin = Math.min(...values);
  const yMax = Math.max(...values);
  const pad = (yMax - yMin) * 0.1 || 1;
  const brushEnabled = data.length > 15;

  return (
    <div className="w-full" style={{ height: brushEnabled ? 200 : 170 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: brushEnabled ? 4 : 12, left: 0 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="#334155" opacity={0.4} />
          <XAxis
            dataKey="ts"
            tickFormatter={(v) => {
              try {
                return new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
              } catch { return ""; }
            }}
            tick={{ fill: "#647087", fontSize: 9 }}
            stroke="#334155"
          />
          <YAxis
            domain={[yMin - pad, yMax + pad]}
            tick={{ fill: "#647087", fontSize: 9 }}
            stroke="#334155"
            width={40}
          />
          <Tooltip content={<ChartTooltip unit={chart?.unit} />} />
          {chart?.min_ok !== null && chart?.min_ok !== undefined && (
            <ReferenceLine y={chart.min_ok} stroke="#f59e0b" strokeDasharray="4 4" strokeWidth={1} />
          )}
          {chart?.max_ok !== null && chart?.max_ok !== undefined && (
            <ReferenceLine y={chart.max_ok} stroke="#f43f5e" strokeDasharray="4 4" strokeWidth={1} />
          )}
          <Line
            type="monotone"
            dataKey="v"
            stroke="#22d3ee"
            strokeWidth={2}
            dot={{ r: 2, fill: "#22d3ee" }}
            activeDot={{ r: 4, fill: "#0891b2" }}
            isAnimationActive={false}
          />
          {brushEnabled && (
            <Brush
              dataKey="ts"
              height={16}
              stroke="#22d3ee"
              travellerWidth={8}
              fill="#0f172a"
              tickFormatter={() => ""}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
